import { extractPlaylistId } from "@/(admin)/modules/ui-builder/config/lib/youtube-helpers"
import type { SiteId } from "@/sites/config"

type BatchRequestType = "posts" | "youtube-videos" | "banners"

type BatchRequestParams = {
  filters?: Record<string, unknown>
  pagination?: Record<string, unknown>
  sort?: Record<string, unknown> | Record<string, unknown>[] | string
  locale?: string
}

export type BatchRequestItem = {
  key: string
  type: BatchRequestType
  filters?: Record<string, unknown>
  pagination?: Record<string, unknown>
  sort?: Record<string, unknown> | Record<string, unknown>[] | string
  locale?: string
}

type PuckNode = {
  type?: string
  props?: Record<string, any>
}

type ExtractBatchRequestsOptions = {
  locale?: string
  siteId?: SiteId
}

type CategoryBannerLayoutTask = {
  componentId: string
  categoryId?: string
  postsPerPage: number
  locale?: string
  siteId?: SiteId
  topBannerId?: string
  bottomBannerId?: string
}

type SectionBannerSidebarTask = {
  componentId: string
  bannerIds: string[]
}

type NewsLayoutBannerTask = {
  componentId: string
  bannerId?: string
}

type VideoLayoutTask = {
  componentId: string
  category: {
    categoryId?: string
    categoryName?: string
    slug?: string
    playlistUrl?: string
  }
}

type HeaderTask = {
  componentId: string
  locale?: string
}

export type TamdaMediaPrefetchPlan = {
  batchRequests: BatchRequestItem[]
  categoryBannerLayouts: CategoryBannerLayoutTask[]
  sectionBannerSidebars: SectionBannerSidebarTask[]
  newsLayoutBanners: NewsLayoutBannerTask[]
  videoCategoryLayouts: VideoLayoutTask[]
  videoCategoryGridLayouts: VideoLayoutTask[]
  headers: HeaderTask[]
  bannerIds: string[]
}

const TAMDA_MEDIA_NEWS_LAYOUT = "TamdaMediaNewsLayout"
const TAMDA_MEDIA_SECTION_BANNER_SIDEBAR = "SectionBannerSidebar"
const TAMDA_MEDIA_CATEGORY_BANNER_LAYOUT = "CategoryBannerLayout"
const TAMDA_MEDIA_VIDEO_CATEGORY_LAYOUT = "VideoCategoryLayout"
const TAMDA_MEDIA_VIDEO_CATEGORY_GRID_LAYOUT = "VideoCategoryGridLayout"
const TAMDA_MEDIA_HEADER = "TamdaMediaHeader"

const getSiteFilters = (siteId?: SiteId) => {
  if (siteId === "tamdamedia.eu") {
    return {
      websites: {
        domain: {
          $eq: "tamdamedia.eu",
        },
      },
    }
  }

  if (siteId === "tamdagroup.eu") {
    return {
      websites: {
        domain: {
          $ne: "tamdamedia.eu",
        },
      },
    }
  }

  return {}
}

const getComponentId = (node: PuckNode) => {
  const componentId = node.props?.id
  return componentId ? String(componentId) : undefined
}

const getCategoryId = (item: any): string | undefined => {
  const categoryId = item?.category?.categoryId ?? item?.categoryId
  if (categoryId === undefined || categoryId === null || categoryId === "") {
    return undefined
  }

  return String(categoryId)
}

const getPlaylistUrl = (category: any) => {
  if (!category || typeof category === "string") {
    return undefined
  }

  return category.playlistUrl ? String(category.playlistUrl) : undefined
}

export const buildTamdaMediaNewsSectionsKey = (componentId: string, categoryId: string) =>
  `tamda-media-news-layout:${componentId}:sections:${categoryId}`

export const buildTamdaMediaNewsSidebarKey = (componentId: string, categoryId: string) =>
  `tamda-media-news-layout:${componentId}:sidebar:${categoryId}`

export const buildTamdaMediaCategoryBannerPostsKey = (componentId: string) =>
  `tamda-media-category-banner-layout:${componentId}:posts`

export const buildTamdaMediaCategoryBannerBannersKey = (componentId: string) =>
  `tamda-media-category-banner-layout:${componentId}:banners`

export const buildTamdaMediaSectionBannerSidebarBannersKey = (componentId: string) =>
  `tamda-media-section-banner-sidebar:${componentId}:banners`

export const buildTamdaMediaNewsLayoutBannerKey = (componentId: string) =>
  `tamda-media-news-layout:${componentId}:banner`

export const buildTamdaMediaVideoCategoryLayoutKey = (componentId: string) =>
  `tamda-media-video-category-layout:${componentId}`

export const buildTamdaMediaVideoCategoryGridLayoutKey = (componentId: string) =>
  `tamda-media-video-category-grid-layout:${componentId}`

export const buildTamdaMediaHeaderKey = (componentId: string) => `tamda-media-header:${componentId}`

const createBatchRequest = ({
  key,
  type,
  requestParams,
}: {
  key: string
  type: BatchRequestType
  requestParams?: BatchRequestParams
}): BatchRequestItem => {
  return {
    key,
    type,
    ...(requestParams?.filters ? { filters: requestParams.filters } : {}),
    ...(requestParams?.pagination ? { pagination: requestParams.pagination } : {}),
    ...(requestParams?.sort ? { sort: requestParams.sort } : {}),
    ...(requestParams?.locale ? { locale: requestParams.locale } : {}),
  }
}

const extractTamdaMediaNewsLayoutRequests = (
  node: PuckNode,
  { locale, siteId }: ExtractBatchRequestsOptions,
): BatchRequestItem[] => {
  const componentId = getComponentId(node)
  if (!componentId) {
    return []
  }

  const siteFilters = getSiteFilters(siteId)
  const categoryIds = Array.isArray(node.props?.categoryIds) ? node.props.categoryIds : []
  const categoryIdsSidebar = Array.isArray(node.props?.categoryIdsSidebar) ? node.props.categoryIdsSidebar : []

  const requests: BatchRequestItem[] = []

  categoryIds.forEach((item: any) => {
    const categoryId = getCategoryId(item)
    if (!categoryId) {
      return
    }

    requests.push(
      createBatchRequest({
        key: buildTamdaMediaNewsSectionsKey(componentId, categoryId),
        type: "posts",
        requestParams: {
          locale,
          sort: "publishedAt:desc",
          pagination: {
            page: 1,
            pageSize: 10,
          },
          filters: {
            categories: {
              id: {
                $in: [categoryId],
              },
            },
            ...siteFilters,
          },
        },
      }),
    )
  })

  categoryIdsSidebar.forEach((item: any) => {
    const categoryId = getCategoryId(item)
    if (!categoryId) {
      return
    }

    requests.push(
      createBatchRequest({
        key: buildTamdaMediaNewsSidebarKey(componentId, categoryId),
        type: "posts",
        requestParams: {
          locale,
          sort: "publishedAt:desc",
          pagination: {
            page: 1,
            pageSize: 8,
          },
          filters: {
            categories: {
              id: {
                $in: [categoryId],
              },
            },
            ...siteFilters,
          },
        },
      }),
    )
  })

  const bannerId = node.props?.banner ? String(node.props.banner) : undefined
  if (bannerId) {
    requests.push(
      createBatchRequest({
        key: buildTamdaMediaNewsLayoutBannerKey(componentId),
        type: "banners",
        requestParams: {
          locale,
          filters: {
            id: {
              $in: [bannerId],
            },
          },
        },
      }),
    )
  }

  return requests
}

const extractSectionBannerSidebarRequests = (
  node: PuckNode,
  options: ExtractBatchRequestsOptions,
): BatchRequestItem[] => {
  const componentId = getComponentId(node)
  if (!componentId) {
    return []
  }

  const categoryIds = Array.isArray(node.props?.categoryIds) ? node.props.categoryIds : []

  return categoryIds
    .map((item: any) => {
      const categoryId = getCategoryId(item)
      if (!categoryId) {
        return undefined
      }

      return createBatchRequest({
        key: buildTamdaMediaNewsSectionsKey(componentId, categoryId),
        type: "posts",
        requestParams: {
          locale: options.locale,
          sort: "publishedAt:desc",
          pagination: {
            page: 1,
            pageSize: 10,
          },
          filters: {
            categories: {
              id: {
                $in: [categoryId],
              },
            },
            ...getSiteFilters(options.siteId),
          },
        },
      })
    })
    .filter(Boolean) as BatchRequestItem[]
}

const extractCategoryBannerLayoutRequests = (
  node: PuckNode,
  options: ExtractBatchRequestsOptions,
): BatchRequestItem[] => {
  const componentId = getComponentId(node)
  if (!componentId) {
    return []
  }

  const categoryId = node.props?.categoryId ? String(node.props.categoryId) : ""
  const postsPerPage = Number(node.props?.postsPerPage || 4)
  const siteFilters = getSiteFilters(options.siteId)

  // Lấy danh sách banner IDs từ props
  const topBannerId = node.props?.topBannerId ? String(node.props.topBannerId) : undefined
  const bottomBannerId = node.props?.bottomBannerId ? String(node.props.bottomBannerId) : undefined
  const bannerIds = [topBannerId, bottomBannerId].filter(Boolean)

  const requests: BatchRequestItem[] = [
    // Request posts
    createBatchRequest({
      key: buildTamdaMediaCategoryBannerPostsKey(componentId),
      type: "posts",
      requestParams: {
        locale: options.locale,
        sort: "publishedAt:desc",
        pagination: {
          page: 1,
          pageSize: postsPerPage,
        },
        filters: categoryId
          ? {
              categories: {
                id: {
                  $in: [categoryId],
                },
              },
              ...siteFilters,
            }
          : {
              ...siteFilters,
            },
      },
    }),
  ]

  // ✅ Thêm request cho banners nếu có
  if (bannerIds.length > 0) {
    requests.push(
      createBatchRequest({
        key: buildTamdaMediaCategoryBannerBannersKey(componentId),
        type: "banners", // Hoặc type nào của bạn
        requestParams: {
          locale: options.locale,
          filters: {
            id: {
              $in: bannerIds,
            },
          },
        },
      }),
    )
  }

  return requests
}

const extractVideoCategoryLayoutRequests = (
  node: PuckNode,
  options: ExtractBatchRequestsOptions,
  keyBuilder: (componentId: string) => string,
): BatchRequestItem[] => {
  const componentId = getComponentId(node)
  if (!componentId) {
    return []
  }

  const category = node.props?.category
  const playlistUrl = getPlaylistUrl(category)
  const playlistId = playlistUrl ? extractPlaylistId(playlistUrl) : ""
  if (!playlistId) {
    return []
  }

  return [
    createBatchRequest({
      key: keyBuilder(componentId),
      type: "youtube-videos",
      requestParams: {
        locale: options.locale,
        sort: "videoPublishedAt:desc",
        pagination: {
          pageSize: 30,
        },
        filters: {
          $and: [
            { disabled: { $eq: false } },
            { videoPublishedAt: { $notNull: true } },
            { playlistId: { $eq: playlistId } },
          ],
        },
      },
    }),
  ]
}

const extractSectionBannerSidebarBannerRequests = (
  node: PuckNode,
  options: ExtractBatchRequestsOptions,
): BatchRequestItem[] => {
  const componentId = getComponentId(node)
  if (!componentId) {
    return []
  }

  // Lấy danh sách banner IDs từ props
  // Props format: bannerIds: Array<{ bannerId: string }>
  const bannerIds = (Array.isArray(node.props?.bannerIds) ? node.props.bannerIds : [])
    .map((item: any) => (item?.bannerId ? String(item.bannerId) : undefined))
    .filter(Boolean) as string[]

  if (bannerIds.length === 0) {
    return []
  }

  return [
    createBatchRequest({
      key: buildTamdaMediaSectionBannerSidebarBannersKey(componentId),
      type: "banners",
      requestParams: {
        locale: options.locale,
        filters: {
          id: {
            $in: bannerIds,
          },
        },
      },
    }),
  ]
}

const batchExtractors: Record<string, (node: PuckNode, options: ExtractBatchRequestsOptions) => BatchRequestItem[]> = {
  [TAMDA_MEDIA_NEWS_LAYOUT]: extractTamdaMediaNewsLayoutRequests,
  [TAMDA_MEDIA_SECTION_BANNER_SIDEBAR]: (node, options) => {
    // ✅ Combine posts requests + banner requests
    const postRequests = extractSectionBannerSidebarRequests(node, options)
    const bannerRequests = extractSectionBannerSidebarBannerRequests(node, options)
    return [...postRequests, ...bannerRequests]
  },
  [TAMDA_MEDIA_CATEGORY_BANNER_LAYOUT]: extractCategoryBannerLayoutRequests,
  [TAMDA_MEDIA_VIDEO_CATEGORY_LAYOUT]: (node, options) =>
    extractVideoCategoryLayoutRequests(node, options, buildTamdaMediaVideoCategoryLayoutKey),
  [TAMDA_MEDIA_VIDEO_CATEGORY_GRID_LAYOUT]: (node, options) =>
    extractVideoCategoryLayoutRequests(node, options, buildTamdaMediaVideoCategoryGridLayoutKey),
}

const isPuckNode = (value: unknown): value is PuckNode => {
  return !!value && typeof value === "object" && typeof (value as PuckNode).type === "string"
}

const walkLayout = (value: unknown, visitor: (node: PuckNode) => void) => {
  if (Array.isArray(value)) {
    value.forEach(item => walkLayout(item, visitor))
    return
  }

  if (!value || typeof value !== "object") {
    return
  }

  if (isPuckNode(value)) {
    visitor(value)
  }

  Object.values(value).forEach(item => {
    walkLayout(item, visitor)
  })
}

export const extractBatchRequests = (
  layout: unknown,
  options: ExtractBatchRequestsOptions = {},
): BatchRequestItem[] => {
  const dedupedRequests = new Map<string, BatchRequestItem>()

  walkLayout(layout, node => {
    const extractRequests = node.type ? batchExtractors[node.type] : undefined
    if (!extractRequests) {
      return
    }

    extractRequests(node, options).forEach(request => {
      dedupedRequests.set(request.key, request)
    })
  })

  return Array.from(dedupedRequests.values())
}

export const extractTamdaMediaPrefetchPlan = (
  layout: unknown,
  options: ExtractBatchRequestsOptions = {},
): TamdaMediaPrefetchPlan => {
  const plan: TamdaMediaPrefetchPlan = {
    batchRequests: extractBatchRequests(layout, options),
    categoryBannerLayouts: [],
    sectionBannerSidebars: [],
    newsLayoutBanners: [],
    videoCategoryLayouts: [],
    videoCategoryGridLayouts: [],
    headers: [],
    bannerIds: [],
  }

  const addBannerIds = (bannerIds: Array<string | undefined>) => {
    bannerIds.filter(Boolean).forEach(id => {
      plan.bannerIds.push(String(id))
    })
  }

  walkLayout(layout, node => {
    const componentId = getComponentId(node)
    if (!componentId || !node.type) {
      return
    }

    if (node.type === TAMDA_MEDIA_CATEGORY_BANNER_LAYOUT) {
      plan.categoryBannerLayouts.push({
        componentId,
        categoryId: node.props?.categoryId ? String(node.props.categoryId) : undefined,
        postsPerPage: Number(node.props?.postsPerPage || 4),
        locale: options.locale,
        siteId: options.siteId,
        topBannerId: node.props?.topBannerId ? String(node.props.topBannerId) : undefined,
        bottomBannerId: node.props?.bottomBannerId ? String(node.props.bottomBannerId) : undefined,
      })
      addBannerIds([node.props?.topBannerId, node.props?.bottomBannerId])
      return
    }

    if (node.type === TAMDA_MEDIA_NEWS_LAYOUT) {
      const bannerId = node.props?.banner ? String(node.props.banner) : undefined
      plan.newsLayoutBanners.push({
        componentId,
        bannerId,
      })
      addBannerIds([bannerId])
      return
    }

    if (node.type === TAMDA_MEDIA_SECTION_BANNER_SIDEBAR) {
      const bannerIds = (Array.isArray(node.props?.bannerIds) ? node.props.bannerIds : [])
        .map((item: any) => (item?.bannerId ? String(item.bannerId) : undefined))
        .filter(Boolean) as string[]

      plan.sectionBannerSidebars.push({
        componentId,
        bannerIds,
      })
      addBannerIds(bannerIds)
      return
    }

    if (node.type === TAMDA_MEDIA_VIDEO_CATEGORY_LAYOUT) {
      plan.videoCategoryLayouts.push({
        componentId,
        category:
          typeof node.props?.category === "string" ? { categoryId: node.props.category } : node.props?.category || {},
      })
      return
    }

    if (node.type === TAMDA_MEDIA_VIDEO_CATEGORY_GRID_LAYOUT) {
      plan.videoCategoryGridLayouts.push({
        componentId,
        category:
          typeof node.props?.category === "string" ? { categoryId: node.props.category } : node.props?.category || {},
      })
      return
    }

    if (node.type === TAMDA_MEDIA_HEADER) {
      plan.headers.push({
        componentId,
        locale: node.props?.locale || options.locale,
      })
    }
  })

  plan.bannerIds = Array.from(new Set(plan.bannerIds))
  return plan
}
